<template>
  <b-row class="m-3">
    <b-col md="8" offset-md="2" lg="6" offset-lg="3">
      <!-- Invalid / expired token -->
      <b-card v-if="tokenError" header="Reset Link Invalid" header-bg-variant="danger" header-text-variant="white" bg-variant="light">
        <p class="mb-3">
          This request verification has expired or has already been used. A verification token may only be used once within 48 hours.
          Please resubmit your request.
        </p>
        <div class="d-flex justify-content-between">
          <a :href="memberPortalUrl" class="btn btn-sm btn-secondary">Cancel</a>
          <a :href="`${memberPortalUrl}/app/account/reset-password`" class="btn btn-sm btn-primary">Resubmit Request</a>
        </div>
      </b-card>

      <!-- Loading token verification -->
      <b-card v-else-if="verifying" header="Verifying…" header-bg-variant="dark" header-text-variant="white" bg-variant="light">
        <div class="text-center py-3"><b-spinner /></div>
      </b-card>

      <!-- Set password form -->
      <b-card v-else header="Reset Your Password" header-bg-variant="dark" header-text-variant="white" bg-variant="light">
        <b-alert show variant="info" class="mb-3">
          <b-icon icon="person-fill" class="mr-2" />
          Resetting password for
          <strong>{{ firstName }} {{ lastName }}</strong>
          .
        </b-alert>

        <b-form @submit.prevent="onSubmit">
          <password-and-confirm-fields
            :password.sync="password"
            :confirm.sync="passwordConfirm"
            :password-state="validated('password')"
            :confirm-state="validated('passwordConfirm')"
            :password-feedback="passwordFeedback || ''"
            password-label="New Password:"
            password-placeholder="New Password"
          />

          <div class="d-flex justify-content-between align-items-center mt-3">
            <b-button size="sm" variant="secondary" :disabled="loading" :to="{ name: 'login' }">Cancel</b-button>
            <b-button size="sm" type="submit" variant="success" :disabled="loading || submitDisabled">
              <b-spinner v-if="loading" small class="mr-1" />
              Reset Password
            </b-button>
          </div>
        </b-form>
      </b-card>

      <recaptcha-v2-modal
        v-model="showV2Challenge"
        :site-key="recaptchaV2SiteKey"
        @resolved="onV2Resolved"
        @cancelled="onV2Cancelled"
        @error="onV2Error"
      />
    </b-col>
  </b-row>
</template>
<script>
import { Vue, Component, Mixins } from 'vue-property-decorator';
import memberDao from '@/dao/member_dao';
import passwordMixin from '@/mixins/password_mixin';
import recaptchaMixin from '@/mixins/recaptcha_fallback';
import PasswordAndConfirmFields from '@/components/PasswordAndConfirmFields';
import RecaptchaV2Modal from '@/components/RecaptchaV2Modal.vue';
import { renderErrorBody } from '@/util/error';

@Component({
  mixins: [passwordMixin],
  components: { PasswordAndConfirmFields, RecaptchaV2Modal },
  metaInfo: {
    title: 'Member Portal - Reset Password'
  }
})
export default class ResetPassword extends Mixins(recaptchaMixin) {
  verifying = true;
  tokenError = null;
  loading = false;
  token = '';
  username = '';
  ubcid = '';
  firstName = '';
  lastName = '';
  email = '';
  password = '';
  passwordConfirm = '';

  get submitDisabled() {
    return !this.validated('password') || !this.validated('passwordConfirm');
  }

  get memberPortalUrl() {
    return this.$store.getters.meta?.memberPortalUrl || 'https://member-portal.carpenters.org';
  }

  validated(field) {
    switch (field) {
      case 'password':
        return !this.passwordValue.length ? undefined : !this.passwordFeedback;
      case 'passwordConfirm':
        return !this.passwordValue ? undefined : (this.passwordConfirm || '').trim().length > 0 && this.password === this.passwordConfirm;
      default:
        return true;
    }
  }

  async mounted() {
    this.loadRecaptcha();
    this.token = this.$route.query.token || '';
    if (!this.token) {
      this.tokenError = 'No reset token was found in the link. Please check your email and try again.';
      this.verifying = false;
      return;
    }
    try {
      const { username, email, ubcid, first_name, last_name } = await memberDao.verifyResetToken(this.token);
      this.username = username;
      this.ubcid = ubcid || '';
      this.firstName = first_name || '';
      this.lastName = last_name || '';
      this.email = email || '';
    } catch (error) {
      this.tokenError = (error && error.message) || 'This password reset link is invalid or has expired.';
    } finally {
      this.verifying = false;
    }
  }

  async onSubmit() {
    if (this.submitDisabled) return;
    try {
      this.loading = true;
      await this.submitWithRecaptcha('reset_password', tokens =>
        memberDao.resetPassword({ token: this.token, password: this.password, ...tokens })
      );
      window.location.href = `${this.memberPortalUrl}/app/login`;
    } catch (error) {
      const body = renderErrorBody(this.$createElement, (error && error.message) || 'An unknown error occurred, please try again.');
      this.$bvModal.msgBoxOk(body, {
        title: 'Password Reset Failed',
        okTitle: 'OK',
        okVariant: 'danger',
        centered: true,
        headerBgVariant: 'danger',
        headerTextVariant: 'white'
      });
    } finally {
      this.loading = false;
    }
  }
}
</script>
<style scoped></style>
